/*
 * Meta Pixel, as on the current site (metaPixel.ts / useMetaPixel.ts), behind one small API.
 * - Loads only on the production hosts and only with REACT_APP_META_PIXEL_ID set.
 * - Writes the `_fbc` (from `fbclid`) and `_fbp` cookies with the legacy names and format.
 * - Every call is a no-op elsewhere (local, preview, tests), so pages can call `track()` freely.
 */
type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  loaded?: boolean;
  version?: string;
  push?: unknown;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

const PIXEL_ID = import.meta.env.REACT_APP_META_PIXEL_ID;
const HOSTS = ['cardnd.com', 'www.cardnd.com'];

export type PixelEvent = 'PageView' | 'Search' | 'ViewContent' | 'InitiateCheckout' | 'Purchase' | 'Lead';

let started = false;

function enabled(): boolean {
  return !!PIXEL_ID && typeof window !== 'undefined' && HOSTS.includes(window.location.hostname);
}

const TWO_YEARS = 60 * 60 * 24 * 730;

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1] ?? '') : null;
}

function writeCookie(name: string, value: string) {
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${TWO_YEARS}; Path=/; SameSite=Lax`;
}

/** `_fbc` from a `fbclid` landing, `_fbp` once per browser (format `fb.1.<ms>.<random>`). */
function ensureCookies() {
  const fbclid = new URLSearchParams(window.location.search).get('fbclid');
  if (fbclid) writeCookie('_fbc', `fb.1.${Date.now()}.${fbclid}`);
  if (!readCookie('_fbp')) writeCookie('_fbp', `fb.1.${Date.now()}.${Math.floor(Math.random() * 1e10)}`);
}

/** Loads fbevents.js and inits the pixel once. Call it at app start. */
export function initAnalytics() {
  if (started || !enabled()) return;
  started = true;
  ensureCookies();
  const w = window;
  if (!w.fbq) {
    const fbq: Fbq = function (...args: unknown[]) {
      if (fbq.callMethod) fbq.callMethod(...args);
      else (fbq.queue ??= []).push(args);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    w.fbq = fbq;
    w._fbq = fbq;
    const s = document.createElement('script');
    s.async = true;
    s.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(s);
  }
  w.fbq('init', PIXEL_ID);
}

/** A fresh event id, to deduplicate the browser event with the server one (`X-Meta-Event-Id`). */
export function newEventId(): string {
  return `ev_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Sends a standard event. `eventId` (Purchase) must equal the `X-Meta-Event-Id` header sent with
 * the reservation. Values are in MAD.
 */
export function track(event: PixelEvent, params: Record<string, unknown> = {}, eventId?: string) {
  if (!started || !window.fbq) return;
  if (eventId) window.fbq('track', event, params, { eventID: eventId });
  else window.fbq('track', event, params);
}

/** The `_fbp` / `_fbc` values to forward to the server with a booking, when present. */
export function metaCookies(): { fbp?: string; fbc?: string } {
  if (typeof document === 'undefined') return {};
  return { fbp: readCookie('_fbp') ?? undefined, fbc: readCookie('_fbc') ?? undefined };
}
